import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useState,
} from "react";
import SmartSearchSelect from "sr/helpers/ui-components/SmartSearchSelect";
import { Checkbox } from "sr/helpers";
import { FormattedMessage, useIntl } from "react-intl";
import { getNearbyRegions } from "sr/utils/api/region/getNearbyRegions";
import { getUserFullProfile } from "sr/utils/api/user/user";
import { addRegion } from "sr/utils/api/user/addRegion";
import { toast } from "react-toastify";
import { getRegion } from "sr/utils/api/user/getRegion";

interface RegionSelectorProps {
  page?: string;
  type?: "add" | "put";
  id?: string;
  dataAddedHandle?: (data: boolean) => void;
}

const RegionSelector: ForwardRefRenderFunction<any, RegionSelectorProps> = (
  { page, type, id, dataAddedHandle },
  ref,
) => {
  const intl = useIntl();

  const [regionId, setRegionId] = useState<any>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [regionList, setRegionList] = useState<any[]>([]);
  const [allRegionsLoaded, setAllRegionsLoaded] = useState(false);
  const [userFullProfile, setUserFullProfile] = useState<any>({});
  const [isEdit, setIsEdit] = useState(false);
  const [primaryLoading, setPrimaryLoading] = useState(false);

  const loadNearbyRegions = async (limit = 10) => {
    if (!regionId) return;

    try {
      setLoading(true);

      const result = await getNearbyRegions({
        ids: [regionId],
        limit,
      });

      const data = result?.data || [];
      setRegionList(data);

      // Once we've requested the max ("View All") tier, there's nothing
      // further to fetch — hide the button regardless of count returned.
      // Otherwise, fewer results than requested also means we're done.
      setAllRegionsLoaded(limit >= 50 || data.length < limit);
    } catch (err) {
      console.error(err);
      setRegionList([]);
      setAllRegionsLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!regionId) {
      setRegionList([]);
      setSelectedRegions([]);
      setAllRegionsLoaded(false);
      return;
    }

    loadNearbyRegions();
  }, [regionId]);

  useEffect(() => {
    (async () => {
      setPrimaryLoading(true);
      const userFullProfileRes = await getUserFullProfile();
      const userProfile = userFullProfileRes?.data;
      setUserFullProfile(userFullProfileRes?.data);
      setRegionId(userProfile?.regionId);
      const res = await getRegion();
      setSelectedRegions(res?.data.map((r: any) => r.regionId.id) || []);

      if (userProfile?.regionId) {
        setIsEdit(true);
      }

      setPrimaryLoading(false);
    })();
  }, []);

  const handleCheckbox = async (regionId: string, checked: boolean) => {
    let updated = [...selectedRegions];

    if (checked) {
      if (!updated.includes(regionId)) {
        updated.push(regionId);
      }
    } else {
      updated = updated.filter((id) => id !== regionId);
    }

    setSelectedRegions(updated);

    const res = await addRegion(regionId);
    if (res) {
      toast.success("Region added successfully");
    }
  };

  const submitForm = async () => {
    const payload = {
      regionId,
      nearbyRegions: selectedRegions,
    };
    dataAddedHandle?.(true);
  };

  React.useImperativeHandle(ref, () => ({
    submitForm,
  }));

  if (primaryLoading) {
    return (
      <div className="text-center py-4">
        <FormattedMessage id="USER.LOADING" defaultMessage="Loading..." />
      </div>
    );
  }

  return (
    <div>
      <SmartSearchSelect
        value={regionId}
        disabled={isEdit}
        options={regionId ? [regionId] : []}
        onChange={(value: any) => setRegionId(value)}
        label={intl.formatMessage({
          id: "INPUT.VALIDATION.REGION",
          defaultMessage: "Region",
        })}
        placeholder="Search for a Region..."
      />
      {regionId && (
        <div className="mt-5 border rounded-md bg-gray-50 p-4">
          <h5 className="font-semibold mb-3">
            <FormattedMessage
              id="AUTH.INPUT.NEARBYREGION.TITLE"
              defaultMessage="Nearby Regions"
            />
          </h5>

          {loading ? (
            <div className="text-center py-4">
              <FormattedMessage
                id="USER.REGION.LOADING"
                defaultMessage="Loading nearby regions..."
              />
            </div>
          ) : regionList.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <FormattedMessage
                id="USER.REGION.NOT.FOUND"
                defaultMessage="No nearby regions found."
              />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto">
                {regionList.map((item: any) => (
                  <div key={item.id} className="border rounded px-2 py-2">
                    <Checkbox
                      id={item.id}
                      value={item.id}
                      checked={selectedRegions.includes(item.id)}
                      disabled={selectedRegions.includes(item.id)}
                      label={`${item.city} (${item.state}), ${item.distanceMiles} mi`}
                      labelClass="font-normal"
                      onChange={(e: any) =>
                        handleCheckbox(item.id, e.target.checked)
                      }
                    />
                  </div>
                ))}
              </div>

              {regionList.length >= 10 && (
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    disabled={allRegionsLoaded}
                    onClick={() => {
                      if (allRegionsLoaded) {
                        toast.info("You've already loaded all nearby regions");
                        return;
                      }
                      loadNearbyRegions(50);
                    }}
                    className={`rounded px-3 py-1 border ${
                      allRegionsLoaded
                        ? "text-gray-400 border-gray-300 cursor-not-allowed"
                        : "text-blue-600 border-blue-600 hover:bg-blue-50"
                    }`}
                  >
                    <FormattedMessage
                      id="AUTH.INPUT.NEARBYREGION.VIEWALL"
                      defaultMessage="View All"
                    />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default forwardRef(RegionSelector);
